/**
 * A VMAX shop's customers (PLAN_PERKS.customers): the people who completed an order with it, the
 * groups they fall in, what they buy elsewhere (only as counts, never who or where) and which of the
 * shop's live cards its customers and followers are after. The groups also aim coupons and emails;
 * pokefans does the matching, the shop sees counts and its own customers only.
 */

/** Groups a shop can aim at. "bought_set:<set>" is everyone who bought something of that set from it. */
export const SEGMENTS = ["all", "new", "repeat", "vip", "recent", "inactive", "followers", "wants_my_stock"] as const;
export type SegmentBase = (typeof SEGMENTS)[number];
export type SegmentKey = SegmentBase | `bought_set:${string}`;
export const BOUGHT_SET = "bought_set:";
export const isSegmentKey = (v: unknown): v is SegmentKey =>
  typeof v === "string" && ((SEGMENTS as readonly string[]).includes(v) || (v.startsWith(BOUGHT_SET) && v.length > BOUGHT_SET.length && v.length <= 140));

/** Not bought in this many days: inactive. Bought within this many: recent. */
export const INACTIVE_DAYS = 60;
export const RECENT_DAYS = 30;
/** VIP: the top tenth by money spent among customers with two orders or more. */
export const VIP_SHARE = 0.1;
/** Interests: purchases of the last days, and a row only with this many different buyers. */
export const INTEREST_DAYS = 180;
export const INTEREST_MIN_BUYERS = 5;
export const INTEREST_ROWS = 5;
export const CUSTOMERS_PAGE_SIZE = 50;
/** Groups and counts about who wants what are shown only from this many people ("menos de 5" below). */
export const MIN_GROUP_SHOWN = 5;
/** Groups the customer list can filter by: never by what people look for or whether they follow (that stays blind). */
export const LIST_SEGMENTS = ["all", "new", "repeat", "vip", "recent", "inactive"] as const;
export const CUSTOMER_SORTS = ["last", "spent", "orders"] as const;
export type CustomerSort = (typeof CUSTOMER_SORTS)[number];

/** Narrowing a group: at least this much spent, this many orders, bought within these days. */
export interface SegmentFilters {
  spentMin: number | null;
  ordersMin: number | null;
  lastDays: number | null;
}
export const NO_FILTERS: SegmentFilters = { spentMin: null, ordersMin: null, lastDays: null };

export function parseSegmentFilters(get: (k: string) => string | null | undefined): SegmentFilters | null {
  const num = (k: string, max: number): number | null | undefined => {
    const v = get(k);
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 && n <= max ? n : undefined;
  };
  const f = { spentMin: num("spentMin", 100_000_000), ordersMin: num("ordersMin", 10_000), lastDays: num("lastDays", 3650) };
  return Object.values(f).includes(undefined) ? null : (f as SegmentFilters);
}

/** A customer as the shop sees them: the name they bought with and their orders with this shop only. */
export interface CustomerRow {
  /** Opaque: to give them a personal coupon. */
  key: string;
  name: string;
  orders: number;
  units: number;
  spentArs: number;
  firstAt: string;
  lastAt: string;
  /** Their latest completed order with the shop. */
  lastOrder: string;
  /** Only groups about their purchases with the shop (never what they look for or whether they follow). */
  segments: SegmentBase[];
}

export interface CustomersView {
  summary: {
    customers: number;
    repeat: number;
    /** Share of customers with two orders or more, and of the money that came from them. */
    repeatPct: number | null;
    repeatRevenuePct: number | null;
    avgTicketArs: number | null;
    followers: number;
  };
  rows: CustomerRow[];
  total: number;
  page: number;
}

/** What the shop's customers buy elsewhere (other shops and pokefans' own store), only as counts. */
export interface CustomerInterests {
  sets: { name: string; buyers: number }[];
  categories: { name: string; buyers: number }[];
  pokemon: { name: string; buyers: number }[];
}

/** The shop's live cards that its customers and followers saved or look for: products, never people. */
export interface CustomerOpportunities {
  /** null: fewer than MIN_GROUP_SHOWN. */
  people: number | null;
  products: { listingId: number; productId: number; name: string; setName: string | null; cardNumber: string | null; people: number | null }[];
}

/** A coupon a buyer can use (personal, or for a group they are in): in "Mis cupones" and at that shop's checkout. */
export interface MyCoupon {
  seller: { handle: string; displayName: string };
  code: string;
  kind: "percent" | "amount";
  value: number;
  minSubtotalArs: number | null;
  maxDiscountArs: number | null;
  endsAt: string | null;
  personal: boolean;
}
